import React from 'react';
import type { ImageSourcePropType } from 'react-native';

import {
  HeroCardBackSide,
  HeroCardFrontSide,
  HeroCardPressable,
} from './components';
import { useHeroCardFlipAnimation } from './hooks';
import { HeroCardLoading } from './HeroCardLoading';

// ─────────────────────────────────────────────────────────────────────────────

export const HeroCard = (props: HeroCardProps) => {
  const { title, imageSrc, imageA11yLabel, ctaLabel, isLoading, url, onPress } =
    props;

  if (isLoading) return <HeroCardLoading />;

  return (
    <HeroCardPressable url={url} onPress={onPress}>
      <HeroCardFrontSide
        title={title}
        imageUrl={imageSrc}
        imageA11yLabel={imageA11yLabel}
        ctaLabel={ctaLabel}
      />
    </HeroCardPressable>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const HeroCardFlip = (props: HeroCardFlipProps) => {
  const {
    title,
    imageSrc,
    imageA11yLabel,
    ctaLabel,
    isLoading,
    backSideTitle,
    backSideDescription,
    backSideInlineLinkLabel,
    backSideInlineLinkURL,
    backSideCtaLabel,
  } = props;

  const {
    toggleFlipState,
    cardFrontSideAnimatedStyle,
    cardBackSideAnimatedStyle,
  } = useHeroCardFlipAnimation();

  if (isLoading) return <HeroCardLoading />;

  return (
    <HeroCardPressable onPress={toggleFlipState}>
      <HeroCardFrontSide
        title={title}
        ctaLabel={ctaLabel}
        imageUrl={imageSrc}
        imageA11yLabel={imageA11yLabel}
        animatedStyle={cardFrontSideAnimatedStyle}
      />

      <HeroCardBackSide
        ctaLabel={backSideCtaLabel}
        description={backSideDescription}
        title={backSideTitle}
        animatedStyle={cardBackSideAnimatedStyle}
        inlineLinkLabel={backSideInlineLinkLabel}
        inlineLinkURL={backSideInlineLinkURL}
      />
    </HeroCardPressable>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type HeroCardProps = {
  readonly title: string;
  readonly imageSrc?: ImageSourcePropType;
  readonly imageA11yLabel?: string;
  readonly ctaLabel: string;
  readonly isLoading?: boolean;
  readonly url?: string;
  readonly onPress?: () => void;
};

type HeroCardFlipProps = Omit<HeroCardProps, 'onPress'> & {
  readonly backSideTitle: string;
  readonly backSideDescription: string;
  readonly backSideCtaLabel: string;
  readonly backSideInlineLinkLabel?: string;
  readonly backSideInlineLinkURL?: string;
};
